import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { makeStyles } from "tss-react/mui";

//All Loading Elements
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
//MUI Elements
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
//Icons
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
//Others
import CommonLayout from "../Common/Layout";
import { showToast } from "../../store/actions";
import { ButtonUI } from "../Common";
import { setPrevState } from "../../util/common";
import feedbackServices from "../../service/feedbackServices";

import ComponentSuspense from "../Common/ComponentSuspense";
const KMKTextEditor = React.lazy(() => import("../Common/KMKTextEditor"));

const useStyles = makeStyles()((theme) => ({
  Header: {
    fontSize: "1.1rem",
    minHeight: "60px !important",
    backgroundColor: "#e8edf1",
    color: "black",
    marginTop: 0,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
  },
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    paddingRight: "20px",
    display: "inline-block",
  },
  inputLabel: {
    fontSize: "1rem",
    fontWeight: "Bold",
    display: "inline-flex",
    "& span": {
      minWidth: "150px",
      [theme.breakpoints.down("sm")]: {
        minWidth: "80px",
      },
    },
  },
  container: {
    padding: 0,
    backgroundColor: "#FFFFFF",
  },
  subContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderTop: 0,
  },
  PanelContainer: {
    padding: "10px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    // minHeight: "250px",
  },
  Panel: {
    padding: "10px 25px",
    backgroundColor: "#FFFFFF",
    marginTop: 0,
    borderRadius: 10,
    boxShadow: "none",
  },
  PanelRow: {
    padding: "12px 0",
  },
}));

const FeatureRequest = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const editorRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [UIVersion, setUIVersion] = useState("N/A");
  const [feedbackState, setFeedbackState] = useState({
    Type: "feedback",
    Title: "",
    Message: "",
  });

  const sendFeedback = async () => {
    setLoading(true);
    const feedback = new feedbackServices();
    const response = await feedback.sendFeedback({
      UIVersion,
      ...feedbackState,
    });
    if (response.success) {
      setFeedbackState({ Type: "feedback", Title: "", Message: "" });
      dispatch(showToast({ message: response.message, type: "success" }));
    }
    setLoading(false);
  };

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        const data = require("../../version.json");
        setUIVersion(data["UI"]);
      } catch (error) {
        setUIVersion("N/A");
      }
      setLoading(false);
    };
    getData();
    return () => {};
  }, []);

  const breadcrumbArr = [
    { link: "/control-panel", displayName: "Home", active: false },
    { link: "#", displayName: "Feedback and Suggestions", active: true },
  ];

  return (
    <CommonLayout title="Feedback and Suggestions" breadcrumbArr={breadcrumbArr}>
      {loading && (
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
          <CircularProgress color="primary" />
        </Backdrop>
      )}
      <AppBar
        sx={{
          position: "relative",
          boxShadow: 0,
          backgroundColor: "#e8edf1",
        }}
      >
        <Toolbar className={classes.Header}>
          <Grid container spacing={5} direction="row" alignItems={"center"}>
            <Grid
              size={{
                sm: 12,
                xs: 12,
              }}
            >
              <Box sx={{ display: "inline-flex", verticalAlign: "sub" }}>
                <Typography className={classes.HeaderTitle} gutterBottom component="div">
                  Feedback and Suggestions
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", height: "calc(100% - 90px)" }}>
        <Box maxWidth="lg" className={classes.subContainer}>
          <Grid container direction="column" justifyContent="flex-end" alignItems="left">
            <Grid className={classes.PanelContainer}>
              <Box className={classes.Panel}>
                <Grid container className={classes.PanelRow} spacing={3}>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 8,
                      md: 4,
                    }}
                  >
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={feedbackState.Type}
                        label="Type"
                        required
                        onChange={(e) => setPrevState(setFeedbackState, { Type: e.target.value })}
                      >
                        <MenuItem value={"feedback"}>Give Feedback</MenuItem>
                        <MenuItem value={"featureRequest"}>Feature Request</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid
                    size={{
                      xs: 0,
                      sm: 4,
                      md: 8,
                    }}
                  ></Grid>
                  <Grid size={12}>
                    <TextField
                      value={feedbackState.Title}
                      inputProps={{ maxLength: 150 }}
                      onChange={(e) => setPrevState(setFeedbackState, { Title: e.target.value })}
                      fullWidth
                      multiline
                      label="Title"
                      variant="outlined"
                      required
                    />
                  </Grid>
                  <Grid sx={{ mb: { sm: "55px", md: "40px" } }} size={12}>
                    <ComponentSuspense>
                      <KMKTextEditor
                        editorRef={editorRef}
                        placeholder={
                          "Share your thoughts on our tools and help us shape the future! Send us your feature enhancement suggestions or new feature requests."
                        }
                        value={feedbackState.Message}
                        setValue={(content) => setPrevState(setFeedbackState, { Message: content })}
                        style={{
                          height: "250px",
                          width: "100%",
                        }}
                        theme="snow"
                      />
                    </ComponentSuspense>
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ mt: "16px", display: "flex", justifyContent: "flex-end" }}>
                <ButtonUI
                  variant="contained"
                  onClick={sendFeedback}
                  color="success"
                  style={{ minWidth: "100px" }}
                  disabled={!feedbackState.Type || !feedbackState.Title || !feedbackState.Message}
                  endIcon={<ChevronRightIcon />}
                >
                  Send
                </ButtonUI>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </CommonLayout>
  );
};

export default FeatureRequest;
